/**
 * Business events the store web app sends to CleverTap and Firebase (epic
 * EXSR-7781). Names and parameter keys are the contract with the Android and
 * iOS apps and the shared KMM layer: do not rename them here alone.
 */
export const STORE_EVENTS = {
  MENU_ITEM_STATUS_CHANGE: 'STORE_MENU_ITEM_STATUS_CHANGE',
  MENU_PRICE_CHANGED: 'STORE_MENU_PRICE_CHANGED',
  ORDER_RECEIVED: 'STORE_ORDER_RECEIVED',
  ORDER_ACCEPTED: 'STORE_ORDER_ACCEPTED',
  ORDER_AUTO_ACCEPTED: 'STORE_ORDER_AUTO_ACCEPTED',
  ORDER_REJECTED: 'STORE_ORDER_REJECTED',
  ORDER_READY: 'STORE_ORDER_READY',
  PREPARATION_TIME_CHANGED: 'STORE_PREPARATION_TIME_CHANGED',
  STATUS_CHANGE: 'STORE_STATUS_CHANGE',
  BUSINESS_HOURS_CHANGED: 'STORE_BUSINESS_HOURS_CHANGED',
  DEAL_CREATED: 'STORE_DEAL_CREATED',
  DEAL_UPDATED: 'STORE_DEAL_UPDATED',
  DEAL_STATUS_CHANGE: 'STORE_DEAL_STATUS_CHANGE',
  DEAL_ITEM_OOS: 'STORE_DEAL_ITEM_OOS'
} as const

export type StoreEventName = typeof STORE_EVENTS[keyof typeof STORE_EVENTS]

// `STATUS` of STORE_MENU_ITEM_STATUS_CHANGE, as written in EXSR-7989 (and sent
// by Android and the KMM layer).
export const MENU_ITEM_STATUS = {
  HIDDEN: 'Hidden',
  UNHIDDEN: 'unhidden'
} as const

// `deal_type` of the STORE_DEAL_* events: the type names the API uses (the
// campaign / deal type of the response `deals` and `affectedDeals`).
export const DEAL_TYPE = {
  FOOD_OFFER: 'food-offer',
  CART_DISCOUNT: 'cart_discount',
  BUY_X_GET_Y: 'buyXGetYOffer'
} as const

export type DealType = typeof DEAL_TYPE[keyof typeof DEAL_TYPE]

// `STATUS` of STORE_DEAL_STATUS_CHANGE, lowercase as written in EXSR-7992.
export const DEAL_STATUS = {
  ACTIVATED: 'activated',
  DEACTIVATED: 'deactivated'
} as const

export type DealStatus = typeof DEAL_STATUS[keyof typeof DEAL_STATUS]

// Values of `updated_fields` of STORE_DEAL_UPDATED: the request body keys of
// each deal endpoint (food-offer, cart_discount, buyXGetYOffer), so a name
// means the same field as in the API. Do not rename: it is the contract with
// whoever builds CleverTap segments on these values.
export const DEAL_FIELD = {
  // food-offer
  AMOUNT: 'amount',
  OFFER_VALID_UNTIL: 'offerValidUntil',
  // cart_discount
  DISCOUNT_TYPE: 'discountType',
  AMOUNT_PERCENTAGE: 'amountPercentage',
  MAX_OFF: 'maxOff',
  TARGET_AMOUNT: 'targetAmount',
  // buyXGetYOffer (also expirationTime, shared with cart_discount)
  ITEM_X_ID: 'itemXId',
  ITEM_Y_ID: 'itemYId',
  NBR_ITEMS_PURCHASED: 'nbrItemsPurchased',
  NBR_ITEMS_OFFER: 'nbrItemsOffer',
  DISCOUNT_PERCENTAGE: 'discountPercentage',
  CUMULATIF: 'cumulatif',
  EXPIRATION_TIME: 'expirationTime'
} as const

/**
 * Parameters Firebase receives under a different key than CleverTap. The
 * shared KMM layer sends the menu item id to Firebase as `Item_ID` while the
 * native CleverTap code sends `item_id` (EXSR-7989: each destination matches
 * its mobile counterpart).
 */
export const FIREBASE_PARAM_KEYS: Partial<
  Record<StoreEventName, Record<string, string>>
> = {
  [STORE_EVENTS.MENU_ITEM_STATUS_CHANGE]: { item_id: 'Item_ID' },
  [STORE_EVENTS.MENU_PRICE_CHANGED]: { item_id: 'Item_ID' }
}
